import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Typography } from '@mui/material';

import { Role, AdminRoles, PatientStatusCode, PatientStatusFilterLabels } from '../../config/DiactiveTypes';
import { getPatientGroupName } from '../../utils/DiactiveUtils';
import { getCachedHospitals } from '../../services/HospitalService';

import HeaderFilterSelect from '../common/HeaderFilterSelect';
import PatientsTableToolbar from './PatientsTableToolbar';
import PatientsTableRow from './PatientsTableRow';
import { MAIL_PENDING_STATUS, usePatientsTableUrlState } from './usePatientsTableUrlState';
import { matchesGroup, matchesHospital, matchesSearch, matchesStatus, sortPatients } from '../../utils/PatientsTableUtils';
import { exportSingleUserFile } from '../../managers/ExcelExporter';

const descDefaulKeys = [
  "createdAt",
  "daysRemaining",
  "postEvalDate",
  "pendingQuestionnaires",
  "pendingGlucoseFiles",
  "workoutsThisWeek",
];

const PATIENTS_SCROLL_KEY = "patientsTableScrollY";

const PatientsTable = ({
  patients,
  role,
  authUserRole = role,
  viewAsDoctor = false,
  doctorModeHospitalId = '',
  patientNavigationState = {},
}) => {
  const navigate = useNavigate();
  const {
    statusCodes,
    selectedHospital,
    setSelectedHospital,
    selectedGroup,
    setSelectedGroup,
    selectedStatus,
    setSelectedStatus,
    searchQuery,
    setSearchQuery,
    sortConfig,
    setSortConfig,
  } = usePatientsTableUrlState({ role, viewAsDoctor, doctorModeHospitalId });

  const [downloadingSingle, setDownloadingSingle] = useState({});
  const [downloadingMulti, setDownloadingMulti] = useState(false);
  const [downloadProgressValue, setDownloadProgressValue] = useState(0);
  const [downloadProgressString, setDownloadProgressString] = useState("");

  const [anchorEl, setAnchorEl] = useState(null); // Anchor for the dropdown menu

  const showMailPendingFilter = AdminRoles.includes(role);

  const matchesStatusOrMail = (patient, statusCode) => {
    if (statusCode === MAIL_PENDING_STATUS) return patient.hasPendingMail === true;
    return matchesStatus(patient, statusCode);
  };

  const patientsForHospitalCounts = patients.filter((patient) => (
    matchesSearch(patient, { role, searchQuery }) &&
    matchesGroup(patient, selectedGroup) &&
    matchesStatusOrMail(patient, selectedStatus)
  ));
  const patientsForGroupCounts = patients.filter((patient) => (
    matchesSearch(patient, { role, searchQuery }) &&
    matchesHospital(patient, selectedHospital) &&
    matchesStatusOrMail(patient, selectedStatus)
  ));

  // Used to build the status dropdown distribution.
  // Important: exclude the status filter itself so multiple statuses can be shown.
  const patientsForStatusCounts = patients.filter((patient) => (
    matchesSearch(patient, { role, searchQuery }) &&
    matchesHospital(patient, selectedHospital) &&
    matchesGroup(patient, selectedGroup)
  ));

  const hospitalPatientCounts = patientsForHospitalCounts.reduce((acc, patient) => {
    const hospitalId = patient.hospital;
    acc[hospitalId] = (acc[hospitalId] ?? 0) + 1;
    return acc;
  }, {});
  const groupPatientCounts = patientsForGroupCounts.reduce((acc, patient) => {
    const groupRole = patient.role;
    acc[groupRole] = (acc[groupRole] ?? 0) + 1;
    return acc;
  }, {});

  const statusPatientCounts = patientsForStatusCounts.reduce((acc, patient) => {
    const code = patient.status?.code ?? "";
    if (!code) return acc;
    acc[code] = (acc[code] ?? 0) + 1;
    return acc;
  }, {});
  const mailPendingCount = patientsForStatusCounts.reduce(
    (acc, patient) => (patient.hasPendingMail === true ? acc + 1 : acc),
    0
  );

  const statusOptions = [
    ...statusCodes
      .filter((code) => code !== MAIL_PENDING_STATUS)
      .map((code) => ({
        value: code,
        label: PatientStatusFilterLabels[code] ?? code,
        count: statusPatientCounts[code] ?? 0,
      })),
    ...(showMailPendingFilter ? [{
      value: MAIL_PENDING_STATUS,
      label: 'Correos pendientes',
      count: mailPendingCount,
      divider: true,
    }] : []),
  ];
  const hospitalFilterOptions = getCachedHospitals().map((hospital) => ({
    value: hospital.id,
    label: hospital.name,
    count: hospitalPatientCounts[hospital.id] ?? 0
  }));
  const groupOptions = Object.keys(groupPatientCounts)
    .sort((a, b) => getPatientGroupName(a).localeCompare(getPatientGroupName(b), "es"))
    .map((groupRole) => ({
      value: groupRole,
      label: getPatientGroupName(groupRole),
      count: groupPatientCounts[groupRole] ?? 0
    }));

  useEffect(() => {
    const savedScrollY = sessionStorage.getItem(PATIENTS_SCROLL_KEY);
    if (savedScrollY !== null) {
      sessionStorage.removeItem(PATIENTS_SCROLL_KEY);
      window.scrollTo(0, parseInt(savedScrollY, 10));
    }
  }, []);

  const filteredPatients = patients.filter((patient) => (
    matchesSearch(patient, { role, searchQuery }) &&
    matchesHospital(patient, selectedHospital) &&
    matchesGroup(patient, selectedGroup) &&
    matchesStatusOrMail(patient, selectedStatus)
  ));

  const sortedPatients = sortPatients(filteredPatients, sortConfig, { selectedStatus });

  const handleHospitalChange = (event) => {
    setSelectedHospital(event.target.value);
  };
  const handleGroupChange = (event) => {
    setSelectedGroup(event.target.value);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key ? (prev.direction === "asc" ? "desc" : "asc") :
      (descDefaulKeys.includes(key) ? "desc" : "asc")
    }));
  };

  const onPatientClick = (patientId) => {
    sessionStorage.setItem(PATIENTS_SCROLL_KEY, String(window.scrollY));
    navigate(`/patient/view/${patientId}`, {
      state: { fromFilteredTable: true, ...patientNavigationState }
    });
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleExportAction = async (exportFunction) => {
    handleMenuClose(); // Close the menu by setting anchorEl to null
    setDownloadingMulti(true); // Start the export process

    try {
      await exportFunction(authUserRole, filteredPatients, onDownloadProgress);
    }
    finally {
      setDownloadingMulti(false); // End the export process
      setDownloadProgressString("");
      setDownloadProgressValue(0);
    }
  };

  const onDownloadProgress = (count, total) => {
    setDownloadProgressValue(Math.round(count/total*100));
    setDownloadProgressString(`${count}/${total}`);
  };

  const onDownloadClick = async (event, patientId) => {
    event.stopPropagation();
    setDownloadingSingle((prev) => ({ ...prev, [patientId]: true }));
    await exportSingleUserFile(authUserRole, patientId);
    setDownloadingSingle((prev) => ({ ...prev, [patientId]: false }));
  };

  const showPersonal = role === Role.SuperUser || role === Role.Doctor;
  const showPostEvalDate = role === Role.Doctor || viewAsDoctor;
  const showPendingDetails = selectedStatus === PatientStatusCode.PRE_PENDING || selectedStatus === PatientStatusCode.POST_PENDING;
  const showNormalDetails = selectedStatus === PatientStatusCode.NORMAL || selectedStatus === PatientStatusCode.LOW_DAYS_REMAINING;
  const baseColumnsCount = AdminRoles.includes(role) ? 8 : 6;
  const columnsCount = baseColumnsCount + (showPostEvalDate ? 1 : 0) + (showPendingDetails ? 2 : 0) + (showNormalDetails ? 1 : 0);

  return (
    <Box>
      <PatientsTableToolbar
        role={role}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        downloadingMulti={downloadingMulti}
        anchorEl={anchorEl}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
        onExportAction={handleExportAction}
        downloadProgressValue={downloadProgressValue}
        downloadProgressString={downloadProgressString}
      />
      <TableContainer>
        <Table sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 170, maxWidth: 170 }}>
                <TableSortLabel
                  active={sortConfig.key === "createdAt"}
                  direction={sortConfig.direction}
                  onClick={() => handleSort("createdAt")}
                >
                  Fecha de alta
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: 160, maxWidth: 160 }}>
                <TableSortLabel
                  active={showPersonal ? sortConfig.key === "name" : sortConfig.key === "nickname"}
                  direction={sortConfig.direction}
                  onClick={() => handleSort(showPersonal ? "name" : "nickname")}
                >
                  Paciente
                </TableSortLabel>
              </TableCell>
              {
                AdminRoles.includes(role) &&
                <TableCell sx={{ display: { xs: "none", sm: "table-cell" }, width: 210, maxWidth: 210 }}>
                  <TableSortLabel
                    active={sortConfig.key === "id"}
                    direction={sortConfig.direction}
                    onClick={() => handleSort("id")}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
              }
              {
                AdminRoles.includes(role) &&
                <TableCell sx={{ display: { xs: "none", sm: "table-cell" }, width: 160, maxWidth: 160 }}>
                  <HeaderFilterSelect
                    label="Hospital"
                    value={selectedHospital}
                    onChange={handleHospitalChange}
                    options={hospitalFilterOptions}
                    totalCount={patientsForHospitalCounts.length}
                    minWidth={0}
                  />
                </TableCell>
              }
              <TableCell sx={{ width: 120, maxWidth: 120 }}>
                <TableSortLabel
                  active={sortConfig.key === "daysRemaining"}
                  direction={sortConfig.direction}
                  onClick={() => handleSort("daysRemaining")}
                >
                  Días restantes
                </TableSortLabel>
              </TableCell>
              {showPostEvalDate && (
                <TableCell sx={{ width: 170, maxWidth: 170 }}>
                  <TableSortLabel
                    active={sortConfig.key === "postEvalDate"}
                    direction={sortConfig.direction}
                    onClick={() => handleSort("postEvalDate")}
                  >
                    Fecha de evaluación post
                  </TableSortLabel>
                </TableCell>
              )}
              <TableCell sx={{ width: 95, maxWidth: 95 }}>
                <HeaderFilterSelect
                  label="Estado"
                  value={selectedStatus}
                  onChange={(event) => {
                    setSelectedStatus(event.target.value);
                  }}
                  options={statusOptions}
                  totalCount={patientsForStatusCounts.length}
                  minWidth={0}
                />
              </TableCell>
              {showPendingDetails && (
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, width: 130, maxWidth: 130 }}>
                  <TableSortLabel
                    active={sortConfig.key === "pendingQuestionnaires"}
                    direction={sortConfig.direction}
                    onClick={() => handleSort("pendingQuestionnaires")}
                  >
                    Cuestionarios
                  </TableSortLabel>
                </TableCell>
              )}
              {showPendingDetails && (
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, width: 110, maxWidth: 110 }}>
                  <TableSortLabel
                    active={sortConfig.key === "pendingGlucoseFiles"}
                    direction={sortConfig.direction}
                    onClick={() => handleSort("pendingGlucoseFiles")}
                  >
                    Archivos
                  </TableSortLabel>
                </TableCell>
              )}
              {showNormalDetails && (
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, width: 100, maxWidth: 100 }}>
                  <TableSortLabel
                    active={sortConfig.key === "workoutsThisWeek"}
                    direction={sortConfig.direction}
                    onClick={() => handleSort("workoutsThisWeek")}
                  >
                    Sesiones
                  </TableSortLabel>
                </TableCell>
              )}
              <TableCell sx={{ width: 120, maxWidth: 120 }}>
                <HeaderFilterSelect
                  label="Grupo"
                  value={selectedGroup}
                  onChange={handleGroupChange}
                  options={groupOptions}
                  totalCount={patientsForGroupCounts.length}
                  minWidth={0}
                />
              </TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, width: 70, maxWidth: 70 }}>Exportar</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedPatients.map((patient) => (
              <PatientsTableRow
                key={patient.id}
                patient={patient}
                role={role}
                viewAsDoctor={viewAsDoctor}
                showPersonal={showPersonal}
                showPostEvalDate={showPostEvalDate}
                showPendingDetails={showPendingDetails}
                showNormalDetails={showNormalDetails}
                sessionsThisWeek={patient.workoutsThisWeek ?? 0}
                downloadingSingle={downloadingSingle}
                onPatientClick={onPatientClick}
                onDownloadClick={onDownloadClick}
              />
            ))}
            {
              sortedPatients.length === 0 &&
              <TableRow>
                <TableCell colSpan={columnsCount} align="center">
                  <Typography variant="body1" color="textSecondary">
                    No hay pacientes
                  </Typography>
                </TableCell>
              </TableRow>
            }
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PatientsTable;